// web/src/screens/admin/switchable_textset.ts
//
// Switchable TextSet widget for "Task instruction(s)" (Task) and
// "Default task instruction(s)" (Game). Lets the author choose between
// linking a shared named TextSet (dropdown) and authoring their own
// inline LV list. Two modes only:
//
//   - "shared"     — dropdown of named TextSets + "Switch to own list".
//   - "non_shared" — inline mountTextSetLVInput + "Switch to shared list".
//
// Initial mode is decided by setInitial(): a non-empty shortCode picks
// "shared", anything else picks "non_shared". The non_shared mode is
// the natural default (matches the pre-switch UX where authors typed
// their own list).

import {
  mountTextSetLVInput,
  type TextSetLVInputHandle,
} from "./ref_inputs.js";

export interface SwitchableTextSetOption {
  short_code: string;
  label: string;
}

export type SwitchableMode = "shared" | "non_shared";

export interface SwitchableTextSetValue {
  mode: SwitchableMode;
  /** When mode === "shared": the selected short_code (empty string = none chosen yet). */
  shortCode?: string;
  /** When mode === "non_shared": the inline LV list (trimmed, blanks dropped). */
  members?: string[];
}

export interface SwitchableTextSetHandle {
  root: HTMLElement;
  value(): SwitchableTextSetValue;
  setInitial(opts: { shortCode?: string | null; members?: string[] }): void;
  refreshOptions(textsets: SwitchableTextSetOption[]): void;
  unmount(): void;
}

export function mountSwitchableTextSetWidget(
  host: HTMLElement,
  opts: { placeholder?: string },
): SwitchableTextSetHandle {
  const wrap = document.createElement("div");
  wrap.setAttribute("style", "display: flex; flex-direction: column; gap: 6px;");
  host.replaceChildren(wrap);

  let mode: SwitchableMode = "non_shared";
  let cachedTextsets: SwitchableTextSetOption[] = [];
  let sharedSelectedSC = "";
  let nonSharedHandle: TextSetLVInputHandle | null = null;
  let nonSharedLVs: string[] = [];
  let disposed = false;

  function makeSwitchButton(label: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.setAttribute("style", "padding: 4px 10px; background: #e5e7eb; color: #111827; border: 0; border-radius: 4px; cursor: pointer; font-size: 12px; align-self: flex-start;");
    b.addEventListener("click", onClick);
    return b;
  }

  function tearDown(): void {
    if (nonSharedHandle) { nonSharedHandle.unmount(); nonSharedHandle = null; }
    wrap.replaceChildren();
  }

  function renderShared(): void {
    tearDown();
    const sel = document.createElement("select");
    sel.setAttribute("style", "padding: 6px 10px; border: 1px solid #d1d5db; border-radius: 4px; font-size: 14px; background: white; max-width: 520px;");
    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = "(unset)";
    sel.appendChild(blank);
    for (const o of cachedTextsets) {
      const opt = document.createElement("option");
      opt.value = o.short_code;
      opt.textContent = o.label;
      sel.appendChild(opt);
    }
    sel.value = sharedSelectedSC;
    sel.addEventListener("change", () => { sharedSelectedSC = sel.value; });
    wrap.appendChild(sel);
    wrap.appendChild(makeSwitchButton("Switch to own list", () => {
      nonSharedLVs = [];
      mode = "non_shared";
      renderNonShared();
    }));
  }

  function renderNonShared(): void {
    tearDown();
    const inner = document.createElement("div");
    wrap.appendChild(inner);
    nonSharedHandle = mountTextSetLVInput(inner, {
      initialLVs: nonSharedLVs,
      placeholder: opts.placeholder,
      hideShortCodeFooter: true,
      onChange: (lvs) => { nonSharedLVs = lvs; },
    });
    wrap.appendChild(makeSwitchButton("Switch to shared list", () => {
      sharedSelectedSC = "";
      mode = "shared";
      renderShared();
    }));
  }

  function rerender(): void {
    if (disposed) return;
    if (mode === "shared") renderShared();
    else renderNonShared();
  }

  rerender();

  return {
    root: wrap,
    value(): SwitchableTextSetValue {
      if (mode === "shared") return { mode, shortCode: sharedSelectedSC };
      return { mode, members: nonSharedHandle ? nonSharedHandle.value() : nonSharedLVs };
    },
    setInitial(init): void {
      const sc = init.shortCode ?? null;
      const lvMembers = init.members ?? [];
      if (sc) {
        sharedSelectedSC = sc;
        nonSharedLVs = [];
        mode = "shared";
      } else {
        nonSharedLVs = lvMembers.slice();
        sharedSelectedSC = "";
        mode = "non_shared";
      }
      rerender();
    },
    refreshOptions(textsets): void {
      cachedTextsets = textsets;
      if (mode === "shared") renderShared();
    },
    unmount(): void {
      disposed = true;
      if (nonSharedHandle) { nonSharedHandle.unmount(); nonSharedHandle = null; }
      host.replaceChildren();
    },
  };
}
